// import node module libraries
import { Row, Col, Card, Form, Button, Image, Alert, InputGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Eye, EyeSlash } from "react-bootstrap-icons";

// import custom hook
import { useMounted } from "hooks/useMounted";

// your AuthContext (adjust handleLogin signature if needed)
import { useAuth } from "context/AuthContext";

// API helpers (cookie login + optional whoami/profile)
import { login as apiLogin} from "services/auth";

const SignIn = () => {
  const [reveal, setReveal] = useState(false);
  const hasMounted = useMounted();
  const navigate = useNavigate();
  const { handleLogin } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const hold = {
    onMouseDown: (e: React.MouseEvent) => { e.preventDefault(); setReveal(true); },
    onMouseUp: () => setReveal(false),
    onMouseLeave: () => setReveal(false),
    onTouchStart: () => setReveal(true),
    onTouchEnd: () => setReveal(false),
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === " " || e.key === "Enter") setReveal(true); },
    onKeyUp: () => setReveal(false),
  };

  // track form state
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (submitting) return;          // guard double submit
    setSubmitting(true);


    try {
      // 1) Cookie login (sets HttpOnly access_token) + attach refresh (sets refresh_token)
      await apiLogin(formData.username, formData.password, formData.rememberMe);
      await handleLogin(); 

      // 2) (Optional) fetch user profile to populate context/state
      //    This uses cookies automatically (credentials: 'include' in helpers)
      // const me = await getCurrentUser().catch(() => null);

      // 3) Update your auth context. Ideally your context shouldn’t require a token anymore.
      //    If handleLogin expects a user object, pass `me`.
      //    If it expects a boolean, pass true. If it *still* expects a token string,
      //    you can pass a placeholder like "cookie" for now and refactor later.
      if (typeof handleLogin === "function") {
        // adjust one of these depending on your context signature:
        // handleLogin(me);         // if your context stores the user object
        // handleLogin(true);       // if your context stores an "isAuthenticated" flag
        handleLogin();      // temporary shim if it expects a token string
      }

      // optional: store in local storage if rememberMe is checked
      if (formData.rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
      }

      navigate("/");
    } catch (err) {
      // console.error("Login error:", err);
      setError("Login failed. Please check your credentials.");
    }
  };

  return (
    <Row className="align-items-center justify-content-center g-0 min-vh-100">
      <Col xxl={4} lg={6} md={8} xs={12} className="py-8 py-xl-0">
        <Card className="smooth-shadow-md">
          <Card.Body className="p-6">
            <div className="mb-4">
              <Link to="/">
                <Image
                  src="/images/brand/logo/dustech_logo.webp"
                  className="mb-2"
                  alt=""
                />
              </Link>
              <p className="mb-6">Please enter your user information.</p>
            </div>

            {hasMounted && (
              <Form onSubmit={handleSubmit}>
                {error && <Alert variant="danger">{error}</Alert>}

                <Form.Group className="mb-3" controlId="username">
                  <Form.Label>Username or email</Form.Label>
                  <Form.Control
                    type="email"
                    name="username"
                    placeholder="Enter address here"
                    required
                    value={formData.username}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>Password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={reveal ? "text" : "password"}
                      name="password"
                      placeholder="**************"
                      required
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <Button
                      {...hold}
                      variant="outline-secondary"
                      type="button"
                      aria-label={reveal ? "Hide password" : "Show password"}
                      title="Press and hold to show"
                    >
                      {reveal ? <EyeSlash /> : <Eye />}
                    </Button>
                  </InputGroup>
                </Form.Group>

                <div className="d-lg-flex justify-content-between align-items-center mb-4">
                  <Form.Check type="checkbox" id="rememberme">
                    <Form.Check.Input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    <Form.Check.Label>Remember me</Form.Check.Label>
                  </Form.Check>
                </div>
                <div>
                  <div className="d-grid">
                    <Button variant="primary" type="submit" disabled={submitting}>
                      {submitting ? "Signing in..." : "Sign In"}
                    </Button>
                  </div>
                  <div className="d-md-flex justify-content-between mt-4">
                    <div className="mb-2 mb-md-0">
                      {/* You can uncomment if you want a signup link */}
                      {/* <Link to="/auth/sign-up" className="fs-5">
                        Create An Account
                      </Link> */}
                    </div>
                    <div>
                      <Link
                        to="/auth/forget-password"
                        className="text-inherit fs-5"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default SignIn;
